import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Controller,
  type FieldErrors,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { FaAnchor } from 'react-icons/fa';
import { MdOutlineMiscellaneousServices } from 'react-icons/md';
import { RxDashboard } from 'react-icons/rx';

import { getServiceId, setServiceId } from '@/api/tokens.helper';
import { imageVariantLink } from '@/common/media';
import { Modal } from '@/components';
import { Button } from '@/components/Button';
import {
  Autocomplete,
  Input,
  LOGO_PICKER_HEIGHT,
  LOGO_PICKER_WIDTH,
  LogoPicker,
  PhoneInput,
} from '@/components/Inputs';
import { RichTextArea } from '@/components/RichTextArea/RichTextArea';
import { UnitInput } from '@/components/UnitInput/UnitInput';
import { useIsServiceProvider } from '@/hooks/useIsServiceProvider';
import { useUnitPreferences } from '@/hooks/useUnitPreferences';
import { useAuth } from '@/store/auth/auth.store';
import { useMarinaServicesStore } from '@/store/marina-services/marina-services.tsx';
import { useCurrentServiceProvider } from '@/store/service-providers/current-service-provider.store';
import { setServerProviderLogo } from '@/store/service-providers/service-providers.api';
import {
  createServiceProviderSchema,
  type CreateUpdateServiceProvidersFormValues,
  ServiceProviderCreatedBy,
  type SingleServiceProvider,
} from '@/store/service-providers/service-providers.dto';
import {
  useCreateServiceProvider,
  useUpdateServiceProvider,
} from '@/store/service-providers/service-providers.store';
import { UserRole } from '@/utils/roles.ts';
import { toast } from '@/utils/toast';
import { toCanonicalFt } from '@/utils/unitConversion';

import { SERVICE_CATEGORIES } from '../DockagePage/helpers/serviceCategories.tsx';

import s from './ServiceProviders.module.scss';
import { ensureUrlProtocol } from './utils/ensureUrlProtocol.ts';

interface SingleValueServiceCatalogSelectProps {
  value?: string | null;
  onChange: (c?: string | null) => void;
}
interface MultipleValuesServiceCatalogSelectProps {
  values: string[];
  onMultipleChange: (c: string) => void;
}

/**
 * A location with no categories comes back as `service_category: null`, and a provider with no
 * locations at all as `locations: null` (`json_agg` over zero rows). Both poison the form's
 * `z.array(z.string())` check — and since nothing renders an error for `service_category`, the
 * only symptom is a Save button that does nothing.
 */
const categoriesOf = (provider?: SingleServiceProvider): string[] => [
  ...new Set(
    (provider?.locations ?? [])
      .flatMap((loc) => loc.service_category ?? [])
      .filter((category): category is string => typeof category === 'string'),
  ),
];

type MarineToggleProps = {
  isMarine?: boolean;
  showMarina?: boolean;
  onMarineToggle?: (next: boolean) => void;
};

export const CreateUpdateServiceProvider = ({
  onClose,
  onSuccess,
  initialData,
  defaultEmail,
  firstService = true,
  closable = true,
  showMarina,
}: {
  onClose: () => void;
  onSuccess: (id: string) => void;
  initialData?: SingleServiceProvider;
  defaultEmail?: string;
  firstService?: boolean;
  closable?: boolean;
  showMarina?: boolean;
}) => {
  const [logo, setLogo] = useState<null | File>(null);
  const [isMarine, setIsMarine] = useState(false);
  const { lengthUnit } = useUnitPreferences();

  const { mutateAsync: createServiceProvider } = useCreateServiceProvider();
  const { mutateAsync: updateServiceProvider } = useUpdateServiceProvider();
  const createMarina = useMarinaServicesStore((store) => store.createMarina);
  const { isServiceProvider } = useIsServiceProvider();
  const authStore = useAuth.getState();
  const queryClient = useQueryClient();

  const methods = useForm<CreateUpdateServiceProvidersFormValues>({
    resolver: zodResolver(createServiceProviderSchema),
    defaultValues: {
      name: initialData?.name ?? '',
      short_description: initialData?.short_description ?? '',
      description: initialData?.description ?? '',
      contact_info: {
        phone: initialData?.contact_info.phone ?? '+1',
        email: initialData?.contact_info.email ?? defaultEmail ?? '',
        website: initialData?.contact_info.website ?? '',
      },
      // No input is rendered for these, so a value the schema rejects can only show up as a Save
      // button that does nothing — coerce rather than trust what came back.
      location: {
        name: initialData?.locations?.[0]?.location?.name ?? 'Name',
        address: initialData?.locations?.[0]?.location?.address ?? '',
        latitude: Number(initialData?.locations?.[0]?.location?.latitude) || 0,
        longitude: Number(initialData?.locations?.[0]?.location?.longitude) || 0,
        map_url: initialData?.locations?.[0]?.location?.map_url ?? 'https://example.com/',
      },
      service_category: categoriesOf(initialData),
      // No input renders these, but the locations row is replaced wholesale on save, so pass
      // the stored value through or it gets wiped.
      certifications: initialData?.locations?.[0]?.certifications ?? [],
      // On edit the existing value wins — `firstService` describes which *create* flow opened
      // the modal and says nothing about a provider that already exists.
      created_by:
        initialData?.created_by ??
        (firstService
          ? ServiceProviderCreatedBy.VESSEL
          : ServiceProviderCreatedBy.SERVICE_PROVIDER),
    },
    mode: 'onChange',
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const contactEmailField = register('contact_info.email', {
    setValueAs: (value) => (typeof value === 'string' ? value.trim() : value),
  });

  // Half the schema has no input rendered for it (created_by, location.*, service_category), so a
  // failure there leaves Save looking dead — nothing submits and no field lights up. Say so.
  const handleInvalidSubmit = (
    validationErrors: FieldErrors<CreateUpdateServiceProvidersFormValues>,
  ) => {
    console.error('Service provider form is invalid:', validationErrors);
    toast.error('Please check the highlighted fields.');
  };

  const handleCreateServiceProvider = async (values: CreateUpdateServiceProvidersFormValues) => {
    const { marine_info, ...providerValues } = values;
    providerValues.contact_info = {
      ...providerValues.contact_info,
      website: ensureUrlProtocol(providerValues.contact_info?.website),
    };
    try {
      let res;
      if (initialData) {
        res = await updateServiceProvider({
          id: initialData.id,
          vesselId: getServiceId(),
          values: providerValues,
          logo,
          // Categories and the address live on this row, not on the provider itself.
          location: initialData.locations?.[0],
        });
      } else {
        res = await createServiceProvider({
          vesselId: getServiceId(),
          values: providerValues,
          logo: !firstService ? null : logo,
        });

        if (!firstService) {
          setServiceId(res.id);
        }

        if (isMarine && res?.id) {
          const marineBody = {
            max_length: Math.round(toCanonicalFt(Number(marine_info?.max_length ?? 0), lengthUnit)),
            max_beam: Math.round(toCanonicalFt(Number(marine_info?.max_beam ?? 0), lengthUnit)),
            shallowest_draft: Math.round(
              toCanonicalFt(Number(marine_info?.shallowest_draft ?? 0), lengthUnit),
            ),
            price_per_foot_daily: `${marine_info?.price_per_foot_daily}${'USD'}`,
            price_per_foot_monthly: `${marine_info?.price_per_foot_monthly}${'USD'}`,
          };
          try {
            // The new provider's id, not getServiceId(): the header is only re-scoped in the
            // own-account flow, and POST /:id/marina is keyed on the provider either way.
            await createMarina(res.id, marineBody);
          } catch (err) {
            console.error('Failed to create marina for service provider:', err);
            toast.error('Couldn’t create the provider’s marina. Please try again.');
          }
        }
      }
      const createdId = res.id;

      if (!initialData && !firstService) {
        try {
          setServiceId(createdId);
          useCurrentServiceProvider.getState().selectServiceProvider(createdId);
          const session = authStore.session;
          if (session) {
            const services = session.services ?? [];
            const hasRole = services.some((r) => r.service_id === createdId);
            const nextServices = hasRole
              ? services
              : ([
                  ...services,
                  {
                    person_id: session.id,
                    service_id: createdId,
                    service_type: 'service-provider',
                    role: UserRole.SERVICE_PROVIDER,
                  },
                ] as typeof services);

            useAuth.setState({
              session: {
                ...session,
                services: nextServices,
              },
            });

            await authStore.updateRoleForVessel(createdId);

            if (logo) {
              await setServerProviderLogo(createdId, logo);
            }
          }

          await authStore.me();
        } catch (err) {
          console.error('Failed to refresh session after creating service provider:', err);
          toast.error('Couldn’t refresh your session. Please reload the page.');
        }
      }

      try {
        await authStore.me();
      } catch (err) {
        console.error('Failed to refresh session after save:', err);
        toast.error('Couldn’t refresh your session. Please reload the page.');
      }

      // Logo is uploaded after the entity is created, so any query that fetched
      // the provider beforehand (e.g. the header avatar) holds stale logo=null.
      await queryClient.invalidateQueries({ queryKey: ['single-service-provider'] });

      if (initialData) {
        toast.success('Service provider updated');
      } else {
        toast.success('Service provider created');
      }

      onSuccess(createdId);
    } catch (err: unknown) {
      console.error(err);
      if (initialData) {
        toast.error('Couldn’t update the service provider. Please try again.');
      } else {
        toast.error('Couldn’t create the service provider. Please try again.');
      }
    }
  };

  useEffect(() => {
    if (categoriesOf(initialData).includes('marina')) {
      setIsMarine(true);
    }
  }, [initialData]);

  return (
    <Modal
      open
      onClose={onClose}
      title={initialData ? 'Edit Service Provider' : 'Add Service Provider'}
      closable={closable}
    >
      <FormProvider {...methods}>
        <form
          className={clsx(s.ModalContent, s.form, 'body-large')}
          onSubmit={handleSubmit(handleCreateServiceProvider, handleInvalidSubmit)}
        >
          <LogoPicker
            compact
            onChange={setLogo}
            initialLogo={imageVariantLink(initialData?.logo, LOGO_PICKER_WIDTH, LOGO_PICKER_HEIGHT)}
          />
          <Input
            label="Service Provider Name"
            mandatory
            placeholder="Chantier Naval Azur"
            error={errors.name?.message}
            {...register('name')}
          />
          <Input
            placeholder="Refit and repair yard in Antibes"
            label="Short Description"
            error={errors.short_description?.message}
            {...register('short_description')}
          />
          <Controller
            name="description"
            control={methods.control}
            render={({ field }) => (
              <RichTextArea
                label="Description"
                placeholder="Description"
                value={field.value ?? ''}
                onChange={field.onChange}
                error={errors.description?.message}
                mandatory
              />
            )}
          />
          <div className={s.row}>
            {/* TODO: Create phone field */}
            <Controller
              name="contact_info.phone"
              render={({ field }) => (
                <PhoneInput
                  label="Mobile Number"
                  onChange={field.onChange}
                  value={field.value}
                  error={errors.contact_info?.phone?.message}
                  className={s.stretch}
                  mandatory
                />
              )}
            />
            <Input
              label="Email"
              placeholder="Email"
              mandatory
              disabled={!!initialData && isServiceProvider}
              error={errors.contact_info?.email?.message}
              {...contactEmailField}
              onBlur={(event) => {
                const trimmedValue = event.target.value.trim();
                event.target.value = trimmedValue;
                setValue('contact_info.email', trimmedValue, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                contactEmailField.onBlur(event);
              }}
            />
          </div>
          <Input
            label="Website"
            placeholder="chantiernavalazur.com"
            error={errors.contact_info?.website?.message}
            {...register('contact_info.website', {
              setValueAs: (value) => (typeof value === 'string' ? value.trim() : value),
            })}
          />

          <AddressInput initialInput={initialData?.locations[0]?.location?.address ?? ''} />
          <FormServiceCategories
            onMarineToggle={setIsMarine}
            isMarine={isMarine}
            showMarina={showMarina}
          />
          {isMarine && <MarineInfo />}
          <Button disabled={isSubmitting} type="submit">
            Save
          </Button>
        </form>
      </FormProvider>
    </Modal>
  );
};

export const ServiceCategoriesSelect = (
  props: (SingleValueServiceCatalogSelectProps | MultipleValuesServiceCatalogSelectProps) &
    MarineToggleProps,
) => {
  const [internalIsMarine, setInternalIsMarine] = useState(false);
  const marineActive = props.isMarine ?? internalIsMarine;

  const handleMarineToggle = () => {
    const next = !marineActive;
    props.onMarineToggle?.(next);
    if (props.isMarine === undefined) {
      setInternalIsMarine(next);
    }
  };

  const multiple = 'values' in props;

  const handler = multiple ? props.onMultipleChange : props.onChange;

  return (
    <section className={s.CategoriesSelector}>
      {!multiple && (
        <span
          className={clsx(props.value === undefined && s.active)}
          onClick={() => props.onChange(undefined)}
        >
          <RxDashboard />
          All Categories
        </span>
      )}
      {props.showMarina && (
        <span className={marineActive ? s.active : ''} onClick={handleMarineToggle}>
          <FaAnchor /> Marine services
        </span>
      )}
      {SERVICE_CATEGORIES.map((c) => (
        <span
          key={c.id}
          className={clsx(
            (multiple ? props.values.includes(c.id) : props.value === c.id) && s.active,
          )}
          onClick={() => handler(c.id)}
        >
          {c.icon}
          {c.title}
        </span>
      ))}
      {!multiple && (
        <span
          className={clsx(props.value === null && s.active)}
          onClick={() => props.onChange(null)}
        >
          <MdOutlineMiscellaneousServices />
          Others
        </span>
      )}
    </section>
  );
};

const FormServiceCategories = ({
  onMarineToggle,
  isMarine,
  showMarina,
}: {
  onMarineToggle?: (next: boolean) => void;
  isMarine?: boolean;
  showMarina?: boolean;
}) => {
  const { setValue } = useFormContext<CreateUpdateServiceProvidersFormValues>();

  const selectedCategories = useWatch({
    name: 'service_category',
  });

  const handleChange = (c: string) => {
    let nextValue;
    if (selectedCategories.includes(c)) {
      nextValue = selectedCategories.filter((category: string) => category !== c);
    } else {
      nextValue = [...selectedCategories, c];
    }
    setValue('service_category', nextValue);
  };

  return (
    <ServiceCategoriesSelect
      values={selectedCategories}
      onMultipleChange={handleChange}
      onMarineToggle={onMarineToggle}
      isMarine={isMarine}
      showMarina={showMarina}
    />
  );
};

export const Address = ({
  place,
  onPlaceChange,
  initialInput,
  label = 'Address',
  disabled,
  mandatory,
}: {
  place: null | PlaceDetails;
  onPlaceChange: (place: null | PlaceDetails) => void;
  initialInput?: string;
  label?: string;
  disabled?: boolean;
  mandatory?: boolean;
}) => {
  const [input, setInput] = useState(initialInput ?? '');
  const [predictions, setPredictions] = useState<null | AutocompletePrediction[]>(null);

  // Keep a ref so the auto-resolve effect always calls the latest callback
  // without needing it as a dep (prevents re-firing on every render)
  const onPlaceChangeRef = useRef(onPlaceChange);
  const placeRef = useRef(place);
  useEffect(() => {
    onPlaceChangeRef.current = onPlaceChange;
    placeRef.current = place;
  });

  const handleInput = async (input: string) => {
    setInput(input);
    onPlaceChange(null);
    setPredictions(null);
    try {
      const predictions = await fetchAutocompletePredictions(input);
      setPredictions(predictions);
    } catch (err: unknown) {
      console.error(err);
    }
  };

  const handlePlaceClick = useCallback(
    async (placeId: null | string) => {
      try {
        const place = placeId ? await fetchPlaceDetails(placeId) : null;
        if (place) {
          onPlaceChange(place);
        } else {
          onPlaceChange(null);
        }
      } catch (err: unknown) {
        console.error(err);
      }
    },
    [onPlaceChange],
  );

  // Auto-resolve initialInput to a PlaceDetails on first non-empty value.
  // Dep is only initialInput — not handlePlaceClick — to avoid re-firing on
  // every render caused by the parent recreating its onChange callback.
  // Skipped when the caller already handed in the place this text came from (a GPS fix, say):
  // autocomplete on the words alone can land on a same-named street kilometres away.
  useEffect(() => {
    if (!initialInput || placeRef.current?.formattedAddress === initialInput) return;
    (async () => {
      const [prediction] = await fetchAutocompletePredictions(initialInput);
      if (prediction) {
        const resolved = await fetchPlaceDetails(prediction.placeId);
        if (resolved) onPlaceChangeRef.current(resolved);
      }
    })();
  }, [initialInput]);

  return (
    <Autocomplete
      label={label}
      placeholder={label}
      disabled={disabled}
      options={
        predictions?.map(({ description, placeId }) => ({ label: description, value: placeId })) ??
        []
      }
      onChange={handleInput}
      value={input}
      selectedOptionLabel={place?.formattedAddress}
      onItemSelect={handlePlaceClick}
      mandatory={mandatory}
    />
  );
};

export const AddressInput = ({ initialInput }: { initialInput: string }) => {
  const [place, setPlace] = useState<null | PlaceDetails>(null);

  const { setValue } = useFormContext<CreateUpdateServiceProvidersFormValues>();

  const handleLocationChange = useCallback(
    (place: null | PlaceDetails) => {
      setPlace(place);
      if (place) {
        setValue('location.address', place.formattedAddress ?? '');
        setValue('location.latitude', place.location?.latitude ?? 0);
        setValue('location.longitude', place.location?.longitude ?? 0);
      } else {
        setValue('location.address', '');
        setValue('location.latitude', 0);
        setValue('location.longitude', 0);
      }
    },
    [setValue],
  );

  return <Address place={place} onPlaceChange={handleLocationChange} initialInput={initialInput} />;
};

const MarineInfo = () => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<CreateUpdateServiceProvidersFormValues>();

  return (
    <div className={s.MarineInfo}>
      <Controller
        name="marine_info.max_length"
        control={control}
        render={({ field }) => (
          <UnitInput
            kind="length"
            label="Max Length"
            type="number"
            placeholder="60"
            error={errors.marine_info?.max_length?.message}
            value={field.value ?? ''}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        name="marine_info.max_beam"
        control={control}
        render={({ field }) => (
          <UnitInput
            kind="length"
            label="Max Beam"
            type="number"
            placeholder="12"
            error={errors.marine_info?.max_beam?.message}
            value={field.value ?? ''}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        name="marine_info.shallowest_draft"
        control={control}
        render={({ field }) => (
          <UnitInput
            kind="length"
            label="Shallowest Draft"
            type="number"
            placeholder="4"
            error={errors.marine_info?.shallowest_draft?.message}
            value={field.value ?? ''}
            onChange={field.onChange}
          />
        )}
      />
      <Input
        label="Price per Foot"
        type="number"
        suffix="$"
        placeholder="3.50"
        error={errors.marine_info?.price_per_foot_daily?.message}
        {...register('marine_info.price_per_foot_daily')}
      />
      <Input
        label="Monthly Price per Foot"
        type="number"
        suffix="$"
        placeholder="45"
        error={errors.marine_info?.price_per_foot_monthly?.message}
        {...register('marine_info.price_per_foot_monthly')}
      />
    </div>
  );
};

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export interface AutocompletePrediction {
  placeId: string;
  description: string;
}

export interface PlaceDetails {
  id: string;
  displayName?: {
    text: string;
  };
  formattedAddress?: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  addressComponents?: Array<{
    types?: string[];
    longText?: string;
    shortText?: string;
    text?: string;
  }>;
}

const fetchAutocompletePredictions = async (input: string): Promise<AutocompletePrediction[]> => {
  if (!input || input.length < 3) {
    return [];
  }

  try {
    const response = await fetch(`https://places.googleapis.com/v1/places:autocomplete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
      },
      body: JSON.stringify({
        input: input,
        includedPrimaryTypes: ['street_address', 'premise', 'establishment'],
      }),
    });

    if (!response.ok) {
      throw new Error(`Places API error: ${response.status}`);
    }

    const data = await response.json();
    const suggestions =
      data.suggestions?.map(
        (s: { placePrediction: { placeId: string; text: { text: string } } }) => ({
          placeId: s.placePrediction.placeId,
          description: s.placePrediction.text.text,
        }),
      ) || [];

    return suggestions;
  } catch (error) {
    console.error('Error fetching autocomplete predictions:', error);
    return [];
  }
};

const fetchPlaceDetails = async (placeId: string): Promise<PlaceDetails | null> => {
  try {
    const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
        'X-Goog-FieldMask': 'id,displayName,formattedAddress,location,addressComponents',
      },
    });

    if (!response.ok) {
      throw new Error(`Places API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching place details:', error);
    return null;
  }
};
